import { DiffEditor } from "@monaco-editor/react";
import cx from "classnames";
import { useCallback } from "react";

import { CodeEditorLanguage } from "components/CodeEditor/helpers";
import FormFieldViewText from "components/FormFields/ViewText";
import ThemedLogs from "components/ThemedLogs";
import ThemedLogsContent from "components/ThemedLogs/Content";
import { Expand } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import FormField from "ds/components/Form/Field";
import Select from "ds/components/Select";
import { BaseSelectItem } from "ds/components/Select/types";
import useTypedContext from "hooks/useTypedContext";
import { ThemeContext } from "views/Theme";

import { EDITOR_CONFIG } from "./constants";
import styles from "./styles.module.css";

type ConfigManagementTreeGridDrawerDiffProps = {
  options?: BaseSelectItem[];
  original: string;
  modified: string;
  language: CodeEditorLanguage;
  toggleFullScreen: (value?: boolean) => void;
  setFileName: (value: string) => void;
  fileName: string;
};

const ConfigManagementTreeGridDrawerDiff = ({
  options,
  original,
  modified,
  language,
  toggleFullScreen,
  setFileName,
  fileName,
}: ConfigManagementTreeGridDrawerDiffProps) => {
  const { isDarkMode, currentDarkModeSyntaxTheme, currentLightModeSyntaxTheme } =
    useTypedContext(ThemeContext);

  const openFullScreen = useCallback(() => {
    toggleFullScreen(true);
  }, [toggleFullScreen]);

  return options?.length ? (
    <>
      {options.length > 1 && (
        <FormField
          className={styles.label}
          label={
            <Box fullWidth align="center" justify="between">
              File
            </Box>
          }
        >
          <Select items={options} value={fileName} onChange={setFileName} />
        </FormField>
      )}
      <FormField
        className={styles.label}
        labelGrow="1"
        label={
          <Box fullWidth align="center" justify="between">
            Diff
            <ButtonIcon variant="ghost" icon={Expand} onPress={openFullScreen}>
              Enter full screen mode
            </ButtonIcon>
          </Box>
        }
      >
        <ThemedLogs className={cx(styles.diffEditor, options.length === 1 && styles.enhanced)}>
          <ThemedLogsContent>
            <Box className={styles.diffContent}>
              <DiffEditor
                original={original}
                modified={modified}
                language={language}
                theme={isDarkMode ? currentDarkModeSyntaxTheme : currentLightModeSyntaxTheme}
                options={{
                  renderSideBySide: false,
                  ...EDITOR_CONFIG,
                }}
                keepCurrentOriginalModel={true}
                keepCurrentModifiedModel={true}
              />
            </Box>
          </ThemedLogsContent>
        </ThemedLogs>
      </FormField>
    </>
  ) : (
    <FormFieldViewText label="Difference" value="No difference available" />
  );
};

export default ConfigManagementTreeGridDrawerDiff;
